'use client';

import React, { useEffect } from 'react';
import { Configure, RefinementList, useRefinementList } from 'react-instantsearch';
import type { UseRefinementListProps } from 'react-instantsearch';
import { useParams } from 'next/navigation';
import aa from 'search-insights';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ErrorBoundary from '../ErrorBoundary';
import ContentModule from '../ContentModule';
import { CollectionProps as LRCollectionProps } from '../Collection';
import sidekick from '@last-rev/cms-sidekick-util';

import { useLocalizationContext } from '../LocalizationContext';

interface CollectionProps extends LRCollectionProps {
  settings?: {
    attribute?: string;
    limit?: number;
    showMore?: boolean;
    searchable?: boolean;
    hideLocaleFilter?: boolean;
  };
}

interface CustomRefinementListProps {
  attribute: string;
  defaultRefinement?: string[];
  transformItems?: UseRefinementListProps['transformItems'];
}

const CustomRefinementList = ({ attribute, defaultRefinement, transformItems }: CustomRefinementListProps) => {
  const { items, refine, canRefine } = useRefinementList({
    attribute,
    transformItems
  });

  const currentRefinement = items.filter((item) => item.isRefined).map((item) => item.value);

  React.useEffect(() => {
    if (currentRefinement.length === 2) {
      refine('false');
    } else if (currentRefinement.length === 0 && defaultRefinement) {
      defaultRefinement.forEach((value) => refine(value));
    }
  }, [currentRefinement.length, defaultRefinement, refine]);

  if (!canRefine) return null;

  return (
    <TranslationFilters>
      {items.map((item) => (
        <label key={item.value} className="ais-RefinementList-label">
          <input
            type="checkbox"
            className="ais-RefinementList-checkbox"
            checked={item.isRefined}
            onChange={() => refine(item.value)}
          />
          <span>{item.label}</span>
        </label>
      ))}
    </TranslationFilters>
  );
};

// Mobile dropdown using MUI Select with multi-select (matches desktop RefinementList behavior)
const MobileFilterDropdown = ({ attribute }: { attribute: string }) => {
  const { items, refine } = useRefinementList({ attribute });
  const selectedValues = items.filter((item) => item.isRefined).map((item) => item.value);

  if (!items.length) return null;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const newValues = event.target.value as string[];
    // Find the item that was toggled (added or removed)
    const added = newValues.find((v) => !selectedValues.includes(v));
    const removed = selectedValues.find((v) => !newValues.includes(v));
    // refine() toggles the refinement for that value
    if (added) {
      refine(added);
    } else if (removed) {
      refine(removed);
    }
  };

  return (
    <Select
      size="small"
      variant="standard"
      displayEmpty
      multiple
      value={selectedValues}
      onChange={handleChange}
      sx={{
        'fontSize': 14,
        'minWidth': 120,
        '& .MuiSelect-select': {
          paddingRight: 3
        }
      }}
      renderValue={(selected) => {
        const selectedArray = selected as string[];
        if (!selectedArray.length) return 'All';
        if (selectedArray.length === 1) {
          const item = items.find((i) => i.value === selectedArray[0]);
          return item ? `${item.label} (${item.count})` : selectedArray[0];
        }
        return `${selectedArray.length} selected`;
      }}>
      {items.map((item) => (
        <MenuItem key={item.value} value={item.value}>
          <Checkbox checked={item.isRefined} size="small" />
          <Typography variant="body2">
            {item.label} ({item.count})
          </Typography>
        </MenuItem>
      ))}
    </Select>
  );
};

export const CollectionSearchFilters = ({ introText, sidekickLookup, settings, ...props }: CollectionProps) => {
  const params = useParams();
  const localization = useLocalizationContext();
  const theme = useTheme();
  // Use media query to determine if we're on mobile - only render ONE Algolia widget at a time
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get locale from route params (set by [locale] dynamic segment)
  const locale = (params?.locale as string) || 'en-US';
  const defaultLocale = 'en-US';

  let userToken: number | string | undefined = undefined;
  useEffect(() => {
    aa('getUserToken', null, (err: Error | null, algoliaUserToken?: number | string) => {
      if (err) {
        console.error(err);
        return;
      }
      userToken = algoliaUserToken;
    });
  }, []);

  return (
    <ErrorBoundary>
      <Box
        data-testid="CollectionSearchFilters"
        sx={{
          'marginTop': { xs: 3, md: 0 },

          '& [class*="Text-root"] b': {
            fontWeight: 500
          }
        }}
        {...sidekick(sidekickLookup)}>
        <Box
          sx={{
            'display': 'flex',
            'alignItems': 'center',

            '& [class*="Text-root"]': {
              'minWidth': 115,

              '& p': {
                'marginBottom': { xs: 0, md: 1 },

                '@media (max-width: 1024px)': {
                  fontSize: 14,
                  textTransform: 'uppercase'
                }
              }
            }
          }}>
          {introText ? (
            <ContentModule
              {...introText}
              {...sidekick(sidekickLookup?.introText)}
              data-testid="CollectionSearchFilters-introText"
            />
          ) : null}
          {/* Mobile: Show Select dropdown (matches Sort by style) */}
          {isMobile && settings?.attribute && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, gap: 0.5 }}>
              <FilterAltIcon sx={{ width: 16, height: 16, color: 'midnight.A40' }} />
              <MobileFilterDropdown attribute={settings.attribute} />
            </Box>
          )}
        </Box>
        {/* Desktop: Show RefinementList checkboxes */}
        {!isMobile && (
          <FilterWrapperComponent attribute={settings?.attribute}>
            <Filters
              attribute={settings?.attribute ?? ''}
              limit={settings?.limit ?? 20}
              showMore={settings?.showMore}
              searchable={settings?.searchable}
              transformItems={(items) =>
                items.map((item) => ({
                  ...item,
                  label: item.label,
                  count: item.count
                }))
              }
            />
            <NoOptionsLabel variant="caption">
              {localization['search.noOptions.label']?.shortTextValue ?? 'No Options Available'}
            </NoOptionsLabel>
          </FilterWrapperComponent>
        )}
        {locale !== defaultLocale && !settings?.hideLocaleFilter && (
          <CustomRefinementList
            attribute="translatedInLocale"
            defaultRefinement={['true']}
            transformItems={(items) =>
              items
                .filter((item) => item.label !== 'true')
                .map((item) => ({
                  ...item,
                  label:
                    localization['search.untranslatedResults.label']?.shortTextValue ?? 'View unstranslated results'
                }))
            }
          />
        )}
        <Configure
          hitsPerPage={8}
          facetingAfterDistinct={true}
          filters={`locale:${locale}`}
          clickAnalytics={true}
          userToken={userToken}
        />
        {/* Locale switcher */}
        {props.items?.map((item) => {
          if ((item as any).__typename === 'NavigationItem') {
            return (
              <LocaleSwitcher key={item.id}>
                <ContentModule {...item} />
              </LocaleSwitcher>
            );
          }
          return null;
        })}
      </Box>
    </ErrorBoundary>
  );
};

interface FilterWrapperComponentProps {
  attribute?: string;
  children: React.ReactNode;
}

const FilterWrapperComponent = ({ attribute, children }: FilterWrapperComponentProps) => {
  if (!attribute) return <FilterWrapper data-no-refinement="true">{children}</FilterWrapper>;

  const { items } = useRefinementList({ attribute });
  const hasNoRefinement = items.length === 0;

  return <FilterWrapper data-no-refinement={hasNoRefinement}>{children}</FilterWrapper>;
};

const FilterWrapper = styled('div', {
  name: 'CollectionSearchFilters',
  slot: 'FilterWrapper'
})(() => ({
  '&[data-no-refinement="true"]': {
    '[class*="CollectionSearchFilters-noOptionsLabel"]': {
      display: 'block'
    }
  }
}));

const Filters = styled(RefinementList, {
  name: 'CollectionSearchFilters',
  slot: 'RefinementList'
})(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none'
  },
  '& .ais-RefinementList-searchBox': {
    'marginBottom': theme.spacing(2),

    '& input': {
      padding: theme.spacing(0.5, 1, 0.5, 3.25),
      border: `1px solid ${theme.palette.midnight.A30}`,
      borderRadius: theme.spacing(2)
    }
  },
  '& .ais-SearchBox-form': {
    position: 'relative'
  },
  '& .ais-SearchBox-submit': {
    position: 'absolute',
    top: '50%',
    left: '6px',
    marginTop: '2px',
    backgroundColor: 'transparent',
    border: 0,
    transform: 'translateY(-50%)'
  },
  '& .ais-SearchBox-reset': {
    marginLeft: theme.spacing(-3),
    backgroundColor: 'transparent',
    border: 0,
    opacity: 0.7,
    transform: 'scale(.8)'
  },
  '& .ais-RefinementList-showMore': {
    'padding': theme.spacing(0.5, 1),
    'backgroundColor': theme.palette.midnight.A09,
    'border': 0,
    'borderRadius': theme.spacing(1),
    'cursor': 'pointer',

    '&:hover': {
      backgroundColor: theme.palette.midnight.A12
    }
  },
  '& .ais-RefinementList-list': {
    listStyle: 'none',
    margin: `0 0 ${theme.spacing(2)}`,
    padding: 0
  },
  '& .ais-RefinementList-item': {
    marginBottom: theme.spacing(1)
  },
  '& .ais-RefinementList-label': {
    ...theme.typography.body3,
    lineHeight: 1.1425
  },
  '& .ais-RefinementList-checkbox': {
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginLeft: 0,
    marginRight: theme.spacing(1),
    accentColor: theme.palette.midnight.A20
  },
  '& .ais-RefinementList-count': {
    'marginLeft': theme.spacing(0.5),
    'color': theme.palette.midnight.A50,
    'fontSize': '0.85em',
    '&::before': {
      content: '"("'
    },
    '&::after': {
      content: '")"'
    }
  },
  '& .ais-RefinementList-noResults': {
    display: 'none'
  }
}));

const LocaleSwitcher = styled(Box, {
  name: 'CollectionSearchFilters',
  slot: 'LocaleSwitcher'
})(({ theme }) => ({
  'display': 'flex',

  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(2)
  },

  '& [class*=NavigationItem-root]': {
    '&:hover': {
      'cursor': 'pointer',
      'backgroundColor': 'transparent',

      '& [class*=NavigationItem-menuRoot]': {
        display: 'block'
      }
    },

    '& a': {
      ...theme.typography.body2,
      'fontWeight': 500,

      '&:hover': {
        textDecoration: 'none'
      },

      [theme.breakpoints.down('md')]: {
        fontSize: 14
      }
    },

    '& [class*=NavigationItem-menuRoot]': {
      'right': 'auto',
      'width': '100%',
      'margin': 0,
      'padding': 0,
      'borderRadius': 0,
      'backgroundColor': theme.palette.midnight.A03,

      '& button': {
        'display': 'block',
        'padding': theme.spacing(1),
        'textAlign': 'left',
        ...theme.typography.body3,

        '&:hover': {
          textDecoration: 'none'
        }
      }
    },

    '& svg': {
      fill: theme.palette.midnight.main,
      marginLeft: theme.spacing(0.5)
    }
  }
}));

const NoOptionsLabel = styled(Typography, {
  name: 'CollectionSearchFilters',
  slot: 'NoOptionsLabel'
})(({ theme }) => ({
  display: 'none', // block
  marginBottom: theme.spacing(1)
}));

const TranslationFilters = styled('div', {
  name: 'CollectionSearchFilters',
  slot: 'TranslationFilters'
})(({ theme }) => ({
  '& .ais-RefinementList-label': {
    ...theme.typography.body3,
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1.1425,
    cursor: 'pointer'
  },
  '& .ais-RefinementList-checkbox': {
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginLeft: 0,
    marginRight: theme.spacing(1),
    accentColor: theme.palette.midnight.A20
  }
}));

export default CollectionSearchFilters;
